import { z } from 'zod';
import {
  ActorRef,
  Email,
  HashKind,
  Id,
  Int64,
  Percent,
  Sha256Hex,
  Timestamp,
  Uri,
  VersionState,
} from './common.js';
import { ThumbnailSet } from './boards.js';
import { UploadFileDescriptor } from './uploads.js';

export const AssetVersion = z.object({
  id: Id,
  asset_id: Id,
  version_number: z.int().min(1),
  state: VersionState,
  original_filename: z.string(),
  mime_type: z.string(),
  size_bytes: Int64,
  sha256: Sha256Hex.nullable().optional(),
  hash_kind: HashKind.optional(),
  hash_verified: z.boolean().optional(),
  width: z.int().nullable().optional(),
  height: z.int().nullable().optional(),
  duration_ms: z.int().nullable().optional(),
  fps: z.number().nullable().optional(),
  codec: z.string().nullable().optional(),
  has_audio: z.boolean().nullable().optional(),
  page_count: z.int().nullable().optional(),
  is_active: z.boolean(),
  is_archived: z.boolean(),
  thumbnails: ThumbnailSet.optional(),
  uploaded_by: Id.nullable().optional(),
  uploader: ActorRef.optional(),
  guest_name: z.string().nullable().optional(),
  guest_email: Email.nullable().optional(),
  uploaded_at: Timestamp,
  processed_at: Timestamp.nullable().optional(),
  error_message: z.string().nullable().optional(),
  retryable: z.boolean().nullable().optional(),
  comment_count: z.int().optional(),
  deleted_at: Timestamp.nullable().optional(),
});
export type AssetVersion = z.infer<typeof AssetVersion>;

export const VersionUploadCreate = UploadFileDescriptor.extend({
  activate_on_ready: z.boolean().default(true),
});
export type VersionUploadCreate = z.infer<typeof VersionUploadCreate>;

export const SpriteFrame = z.object({
  t_ms: z.int(),
  x: z.int(),
  y: z.int(),
});
export type SpriteFrame = z.infer<typeof SpriteFrame>;

/** `sprite.json` (03 §6.1, server spec §3.5 step 3). */
export const SpriteIndex = z.object({
  v: z.literal(1).optional(),
  tile_w: z.int(),
  tile_h: z.int(),
  cols: z.int(),
  rows: z.int(),
  count: z.int(),
  duration_ms: z.int(),
  frames: z.array(SpriteFrame),
});
export type SpriteIndex = z.infer<typeof SpriteIndex>;

export const HlsRenditionHeight = z.union([
  z.literal(240),
  z.literal(480),
  z.literal(720),
  z.literal(1080),
  z.literal(2160),
]);
export type HlsRenditionHeight = z.infer<typeof HlsRenditionHeight>;

export const HlsRendition = z.object({
  height: HlsRenditionHeight,
  bandwidth: z.int(),
});
export type HlsRendition = z.infer<typeof HlsRendition>;

/** Signed delivery URLs for one version (06 §5). All expire at `expires_at` (1 h). */
export const MediaUrls = z.object({
  version_id: Id,
  expires_at: Timestamp,
  thumbnails: ThumbnailSet,
  preview_url: Uri.nullable().optional(),
  original: Uri.nullable().optional(),
  hls_manifest_url: Uri.nullable().optional(),
  hls_renditions: z.array(HlsRendition).optional(),
  poster_url: Uri.nullable().optional(),
  sprite_sheet_url: Uri.nullable().optional(),
  sprite_index_url: Uri.nullable().optional(),
  sprite_index: SpriteIndex.nullable().optional(),
  preview_mp4_url: Uri.nullable().optional(),
  waveform_url: Uri.nullable().optional(),
  audio_preview_url: Uri.nullable().optional(),
  transcript_url: Uri.nullable().optional(),
  pdf_url: Uri.nullable().optional(),
});
export type MediaUrls = z.infer<typeof MediaUrls>;

export const VersionCompareResponse = z.object({
  a: AssetVersion,
  b: AssetVersion,
  same_fps: z.boolean(),
  same_dimensions: z.boolean().optional(),
  media_a: MediaUrls,
  media_b: MediaUrls,
  diff: z
    .record(z.string(), z.object({ a: z.unknown().optional(), b: z.unknown().optional() }))
    .optional(),
});
export type VersionCompareResponse = z.infer<typeof VersionCompareResponse>;

export const ProcessingStepName = z.enum([
  'probe',
  'renditions',
  'hls',
  'poster',
  'sprite',
  'scrub_mp4',
  'waveform',
  'audio_extract',
  'transcript',
  'embeddings',
  'tags',
  'regions',
  'ocr',
  'phash',
  'dominant_colors',
  'hash_verify',
  'describe', // release4/E
]);
export type ProcessingStepName = z.infer<typeof ProcessingStepName>;

export const ProcessingStepState = z.enum(['pending', 'running', 'done', 'failed', 'skipped']);
export type ProcessingStepState = z.infer<typeof ProcessingStepState>;

export const ProcessingStep = z.object({
  name: ProcessingStepName,
  state: ProcessingStepState,
  progress: Percent.nullable().optional(),
  started_at: Timestamp.nullable().optional(),
  finished_at: Timestamp.nullable().optional(),
  error: z.string().nullable().optional(),
});
export type ProcessingStep = z.infer<typeof ProcessingStep>;

export const ProcessingQueue = z.enum([
  'process-image',
  'process-video',
  'process-audio',
  'process-document',
  'ai-index',
]);
export type ProcessingQueue = z.infer<typeof ProcessingQueue>;

export const ProcessingStatus = z.object({
  version_id: Id,
  asset_id: Id.optional(),
  state: VersionState,
  progress: Percent,
  queue: ProcessingQueue.nullable().optional(),
  steps: z.array(ProcessingStep),
  error_message: z.string().nullable().optional(),
  retryable: z.boolean().optional(),
  queued_at: Timestamp.nullable().optional(),
  started_at: Timestamp.nullable().optional(),
  finished_at: Timestamp.nullable().optional(),
});
export type ProcessingStatus = z.infer<typeof ProcessingStatus>;

/** Inline body of `POST /versions/{id}/reprocess` (not a named component). */
export const ReprocessStepName = z.enum([
  'renditions',
  'hls',
  'sprite',
  'waveform',
  'transcript',
  'embeddings',
  'tags',
  'regions',
  'ocr',
]);
export type ReprocessStepName = z.infer<typeof ReprocessStepName>;
export const ReprocessRequest = z.object({
  steps: z.array(ReprocessStepName).optional(),
});
export type ReprocessRequest = z.infer<typeof ReprocessRequest>;

export const DownloadResponse = z.object({
  url: Uri.nullable().optional(),
  filename: z.string().nullable().optional(),
  size_bytes: Int64.nullable().optional(),
  expires_at: Timestamp.nullable().optional(),
  job_id: Id.nullable().optional(),
});
export type DownloadResponse = z.infer<typeof DownloadResponse>;

/** `export_render` (Release 3 EXP-1): one on-demand image export; `result.url` is the rendition. `export_csv` (release4/X, EXP-2): metadata CSV of selected assets. */
export const JobKind = z.enum(['zip_download', 'audit_export', 'workspace_delete', 'export_render', 'export_csv']);
export type JobKind = z.infer<typeof JobKind>;
export const JobState = z.enum(['queued', 'running', 'done', 'failed']);
export type JobState = z.infer<typeof JobState>;

export const Job = z.object({
  id: Id,
  kind: JobKind,
  state: JobState,
  progress: Percent,
  result: DownloadResponse.nullable().optional(),
  error_message: z.string().nullable().optional(),
  created_at: Timestamp,
  finished_at: Timestamp.nullable().optional(),
});
export type Job = z.infer<typeof Job>;

// --- release4/T --- transcript export (REV-3): `?format=txt|srt|vtt`
export const TranscriptExportFormat = z.enum(['txt', 'srt', 'vtt']);
export type TranscriptExportFormat = z.infer<typeof TranscriptExportFormat>;
